/**
 * P1 4 Mrt 2026 — Gedeeld paneel voor Klant ↔ Factuur koppeling.
 *
 * Gebruikt in twee schermen:
 *   - `InvoiceDetailDialog` (read + acties zonder de factuur te openen in edit)
 *   - `InvoiceEditDialog`   (inline binnen factuur-bewerkflow)
 *
 * Props:
 *   invoice   — factuur-object (moet `id`, `linked_customer`,
 *               `_suggested_matches` bevatten indien beschikbaar)
 *   canManage — bool. Alleen Owner/Admin/Manager mogen koppelen/ontkoppelen.
 *   onLinked  — callback na koppel/ontkoppel actie (voor refresh)
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  User, Link2, Unlink, Search, Loader2, CheckCircle2, ExternalLink,
} from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { adminApi } from "../../../context/AuthContext";
import EnterpriseEntityPicker from "../shared/EnterpriseEntityPicker";

export default function InvoiceCustomerLinkPanel({
  invoice,
  canManage,
  onLinked,
}) {
  const [linkedCustomer, setLinkedCustomer] = useState(invoice.linked_customer || null);
  const [suggestions, setSuggestions] = useState(invoice._suggested_matches || []);
  const [linkBusy, setLinkBusy] = useState(false);

  useEffect(() => {
    setLinkedCustomer(invoice.linked_customer || null);
    setSuggestions(invoice._suggested_matches || []);
  }, [invoice.id, invoice.linked_customer, invoice._suggested_matches]);

  const doLinkCustomer = async (customerId, matchType = "manual", confidence = null) => {
    setLinkBusy(true);
    try {
      const r = await adminApi.post(
        `/admin/sales-invoices/${invoice.id}/link-customer`,
        { customer_id: customerId, match_type: matchType, confidence },
      );
      setLinkedCustomer(r.data.linked_customer);
      setSuggestions([]);
      toast.success("Klant gekoppeld aan factuur");
      if (onLinked) await onLinked();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Koppelen mislukt");
    } finally {
      setLinkBusy(false);
    }
  };

  const doUnlinkCustomer = async () => {
    setLinkBusy(true);
    try {
      await adminApi.delete(`/admin/sales-invoices/${invoice.id}/link-customer`);
      setLinkedCustomer(null);
      try {
        const r = await adminApi.get(`/admin/sales-invoices/${invoice.id}/customer-matches`);
        setSuggestions(r.data.suggestions || []);
      } catch (_) { /* niet fataal */ }
      toast.success("Klant ontkoppeld");
      if (onLinked) await onLinked();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ontkoppelen mislukt");
    } finally {
      setLinkBusy(false);
    }
  };

  const openCustomerDetail = () => {
    if (!linkedCustomer?.id) return;
    window.location.assign(`/admin?section=klanten&customer_id=${linkedCustomer.id}`);
  };

  return (
    <div className="bg-white border border-gray-200 p-3 sm:p-4" data-testid="invoice-customer-link-section">
      <h3 className="text-xs uppercase font-bold tracking-wide text-gray-700 mb-3 flex items-center gap-2">
        <User className="w-4 h-4 text-[#0055FF]" /> Gekoppelde klant
      </h3>

      {linkedCustomer ? (
        <div className="flex flex-col sm:flex-row sm:items-start gap-3 bg-emerald-50 border border-emerald-200 p-3"
             data-testid="invoice-linked-customer-card">
          <div className="flex-1 min-w-0">
            <div className="font-chivo font-bold text-sm text-emerald-900 flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate" data-testid="invoice-linked-customer-name">
                {linkedCustomer.name || linkedCustomer.company || "—"}
              </span>
            </div>
            {linkedCustomer.company && (
              <div className="text-xs text-emerald-800 mt-0.5 truncate" data-testid="invoice-linked-customer-company">
                {linkedCustomer.company}
              </div>
            )}
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-emerald-800 mt-0.5">
              {linkedCustomer.email && (
                <span className="truncate" data-testid="invoice-linked-customer-email">
                  {linkedCustomer.email}
                </span>
              )}
              {linkedCustomer.phone && (
                <span className="truncate" data-testid="invoice-linked-customer-phone">
                  {linkedCustomer.phone}
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button
              type="button" variant="outline" size="sm"
              onClick={openCustomerDetail}
              className="rounded-none text-xs h-8"
              data-testid="invoice-open-customer-btn"
            >
              <ExternalLink className="w-3 h-3 mr-1" /> Klant openen
            </Button>
            {canManage && (
              <Button
                type="button" variant="outline" size="sm"
                onClick={doUnlinkCustomer}
                disabled={linkBusy}
                className="rounded-none text-xs h-8 border-red-200 text-red-700 hover:bg-red-50"
                data-testid="invoice-unlink-customer-btn"
              >
                {linkBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Unlink className="w-3 h-3 mr-1" />}
                Ontkoppelen
              </Button>
            )}
          </div>
        </div>
      ) : (
        <>
          {suggestions.length > 0 ? (
            <div className="mb-3" data-testid="invoice-customer-suggestions">
              <div className="text-[10px] uppercase font-bold text-gray-600 mb-2">
                Mogelijke klantmatches ({suggestions.length})
              </div>
              <div className="space-y-1.5">
                {suggestions.map((s) => (
                  <div key={s.customer_id}
                       className="flex flex-col sm:flex-row sm:items-center gap-2 bg-blue-50 border border-blue-200 p-2 text-xs"
                       data-testid={`invoice-suggestion-${s.customer_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-blue-900 truncate">
                        {s.name || s.company || "—"}
                      </div>
                      <div className="text-blue-800 truncate">
                        {[s.company, s.email, s.phone].filter(Boolean).join(" · ")}
                      </div>
                      <div className="text-[10px] text-blue-700 mt-0.5">
                        {s.match_type.replace(/_/g, " ")} · {s.confidence}
                      </div>
                    </div>
                    {canManage && (
                      <Button
                        type="button" size="sm"
                        onClick={() => doLinkCustomer(s.customer_id, s.match_type, s.confidence)}
                        disabled={linkBusy}
                        className="rounded-none text-xs bg-[#0055FF] hover:bg-[#0044dd] h-8 self-start sm:self-auto"
                        data-testid={`invoice-link-suggestion-${s.customer_id}`}
                      >
                        <Link2 className="w-3 h-3 mr-1" /> Koppel klant
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-xs text-gray-500 mb-3" data-testid="invoice-no-customer-hint">
              Geen klant gekoppeld.
            </div>
          )}

          {canManage && (
            <div className="mt-2" data-testid="invoice-customer-search-wrapper">
              {/* Sprint A1 (22 Feb 2026) — vervangen door
                  EnterpriseEntityPicker. De custom Enter-to-search flow +
                  eigen result-list is niet meer nodig; de picker doet
                  live debounced search. */}
              <EnterpriseEntityPicker
                entityType="customer"
                mode="inline"
                testIdPrefix="invoice-customer"
                autoFocus={false}
                showRecent={true}
                onSelect={(item) => doLinkCustomer(item.id, "manual", null)}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
